import "../styles/globals.css";
import { Provider as JotaiProvider } from "jotai";
import { NextPage } from "next";
import { useRouter } from "next/router";
import { ReactElement, ReactNode, useEffect } from "react";

import { SystemMessageProvider } from "@/components/contexts/SystemMessageContext";
import Layout from "@/components/templates/Layout";
import GlobalLoading from "@/components/templates/layout/GlobalLoading";
import { useUnauthenticatedHandler } from "@/hooks/useUnauthenticatedHandler";
import { usePageView } from "@/libs/gtag";
import Error400Page from "@/pages/400";
import Error404Page from "@/pages/404";
import Error500Page from "@/pages/500";
import { useInitialize, useSetUnauthenticated } from "@/states/auth/operations";
import { useAuthUserUiState } from "@/states/auth/selectors";
import { useClearError } from "@/states/error/operations";
import { useErrorApiUiState } from "@/states/error/selectors";
import {
  addCustomEventListener,
  CustomEventType,
  removeCustomEventListener,
} from "@/utils/custom-event";

import type { AppProps } from "next/app";

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  // gaタグ
  usePageView();
  const getLayout = Component.getLayout || ((page) => <Layout>{page}</Layout>);

  return (
    <Provider>
      <ErrorSwitch>
        <AuthInitializer>
          {getLayout(<Component {...pageProps} />)}
        </AuthInitializer>
      </ErrorSwitch>
    </Provider>
  );
}

function Provider({ children }: { children: ReactNode }) {
  return (
    <JotaiProvider>
      <SystemMessageProvider>
        <GlobalLoading />
        {children}
      </SystemMessageProvider>
    </JotaiProvider>
  );
}
// NOTE: サーバ側に対応していないので、Next.js 14でMiddlewareが利用できそうなら実装変更を検討する
function AuthInitializer({ children }: { children: ReactNode }): JSX.Element {
  const authUser = useAuthUserUiState();
  const handleUnauthenticated = useUnauthenticatedHandler();
  const errorState = useErrorApiUiState();
  const initialize = useInitialize();
  const setUnauthenticated = useSetUnauthenticated();
  const router = useRouter();

  useEffect(() => {
    if (authUser.status === "Uninitialized") {
      void (async () => {
        await initialize();
        return;
      })();
    }

    // APIで401が出たときの処理（Axiosのインターセプターからカスタムイベントで伝播）
    const handleApiUnauthenticated = () => {
      setUnauthenticated();
      void handleUnauthenticated();
    };

    addCustomEventListener(
      CustomEventType.ApiUnauthenticated,
      handleApiUnauthenticated,
    );
    return () => {
      removeCustomEventListener(
        CustomEventType.ApiUnauthenticated,
        handleApiUnauthenticated,
      );
    };
  }, [
    authUser,
    errorState,
    router,
    initialize,
    handleUnauthenticated,
    setUnauthenticated,
  ]);

  useEffect(() => {
    if (errorState.status !== "NotError") {
      return;
    }

    void handleUnauthenticated();
  }, [authUser, errorState, router, handleUnauthenticated]);

  return <>{children}</>;
}

function ErrorSwitch({ children }: { children: ReactNode }): JSX.Element {
  const router = useRouter();
  const errorState = useErrorApiUiState();
  const clearError = useClearError();

  useEffect(() => {
    const handleStop = () => {
      clearError();
    };

    router.events.on("routeChangeComplete", handleStop);

    return () => {
      router.events.off("routeChangeComplete", handleStop);
    };
  }, [router, clearError]);

  switch (errorState.status) {
    case "Error400": {
      const getLayoutError400 =
        Error400Page.getLayout ?? ((page) => <>{page}</>);
      return getLayoutError400(<Error400Page error={errorState.error} />);
    }
    case "Error404": {
      const getLayoutError404 =
        Error404Page.getLayout ?? ((page) => <>{page}</>);
      return getLayoutError404(<Error404Page />);
    }
    case "Error500": {
      const getLayoutError500 =
        Error500Page.getLayout ?? ((page) => <>{page}</>);
      return getLayoutError500(<Error500Page />);
    }
    default:
      return <>{children}</>;
  }
}

export default MyApp;
